import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, ShieldCheck, Clock, AlertTriangle, CheckCircle2, Lock, Search } from "lucide-react";

const STATUS_TONE = {
  verified: "border-good bg-good/10 text-good",
  pending_verification: "border-warn bg-warn/10",
  unverified: "border-warn bg-warn/10",
};

/**
 * Admin-only Payment Rails. Bank numbers are entered here and nowhere else.
 * A rail stays DORMANT until it is both micro-deposit verified and explicitly
 * authorized for payouts.
 */
export const PaymentRails = ({ employee, onChanged }) => {
  const empId = employee?.id;
  const [rail, setRail] = useState(null);
  const [form, setForm] = useState({ routing_number: "", account_number: "", account_holder_name: "", account_type: "checking" });
  const [lookup, setLookup] = useState(null);
  const [amounts, setAmounts] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRail((await api.get(`/employees/${empId}/rail`)).data); } catch { /* admin-only */ }
  }, [empId]);
  useEffect(() => { load(); }, [load]);

  // Real-time routing resolution — debounced so we don't hit the directory on
  // every keystroke.
  useEffect(() => {
    const rn = form.routing_number.replace(/\D/g, "");
    setLookup(null);
    if (rn.length !== 9) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/routing/${rn}`, { params: { state: employee?.state || "" } });
        setLookup(data);
      } catch { /* non-blocking */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.routing_number, employee?.state]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${empId}/bank/manual/start`, form);
      toast.success(`Bank ••••${data.last4} saved`, { description: data.message });
      setForm({ routing_number: "", account_number: "", account_holder_name: "", account_type: "checking" });
      setLookup(null);
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cents = [Math.round(parseFloat(amounts.a) * 100), Math.round(parseFloat(amounts.b) * 100)];
      const { data } = await api.post(`/employees/${empId}/bank/manual/verify`, { amounts: cents });
      toast.success("Micro-deposits confirmed", { description: data.message });
      setAmounts({ a: "", b: "" });
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const authorize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/employees/${empId}/rail/authorize`, {});
      toast.success("Rail authorized for payouts", { description: data.message });
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(`/employees/${empId}/rail/revoke`, {});
      toast.success("Rail returned to dormant — payouts blocked");
      await load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  if (!rail) return null;
  const StatusIcon = rail.authorized ? CheckCircle2 : rail.verification === "pending_verification" ? Clock : AlertTriangle;

  return (
    <div className="hard-card p-6 mt-8" data-testid="payment-rails">
      <div className="overline mb-3 flex items-center gap-2"><Landmark className="w-4 h-4" /> § PAYMENT RAILS · ADMIN ONLY</div>

      <div className={`border-2 p-4 mb-5 flex items-start gap-3 ${rail.authorized ? "border-good bg-good/10" : STATUS_TONE[rail.verification] || "border-ink"}`} data-testid="rail-status">
        <StatusIcon className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="font-mono text-xs leading-relaxed">
          <b className="uppercase tracking-widest text-[10px] block mb-1" data-testid="rail-status-label">
            {rail.authorized ? "Authorized for payouts" : rail.rail_state === "dormant" ? "Dormant — not payable" : rail.badge}
          </b>
          {rail.account_masked ? `Account ${rail.account_masked} · routing ${rail.routing_masked}` : "No bank account on file"}
          {rail.bank_name ? ` · ${rail.bank_name}` : ""}
          {rail.authorized_by ? ` · authorized by ${rail.authorized_by}` : ""}
          <div className="text-muted-foreground mt-1">{rail.explain}</div>
        </div>
        <div className="ml-auto shrink-0 flex flex-col gap-2">
          {rail.can_authorize && !rail.authorized && (
            <button onClick={authorize} disabled={busy} data-testid="rail-authorize-btn" className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
              Authorize for Payouts
            </button>
          )}
          {rail.authorized && (
            <button onClick={revoke} disabled={busy} data-testid="rail-revoke-btn" className="border-2 border-bad text-bad px-4 py-2 font-mono uppercase text-[10px] tracking-widest hover:bg-bad hover:text-white disabled:opacity-40">
              Make Dormant
            </button>
          )}
        </div>
      </div>

      {rail.nexus_alert && (
        <div className="border-2 border-warn bg-warn/10 p-3 mb-5 flex items-start gap-2" data-testid="nexus-alert">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-warn" />
          <div className="font-mono text-[11px] leading-relaxed">
            <b className="uppercase tracking-widest text-[10px] block mb-0.5">External Nexus Alert</b>
            {rail.nexus_note}
          </div>
        </div>
      )}

      {rail.verification === "pending_verification" ? (
        <form onSubmit={verify} className="flex flex-wrap items-end gap-3" data-testid="rail-verify-form">
          <div className="font-mono text-[11px] text-muted-foreground w-full leading-relaxed">
            Two micro-deposits are on their way to {rail.account_masked}. Enter both amounts from the bank
            statement to prove the account.
          </div>
          {[["a", "Deposit 1 ($)"], ["b", "Deposit 2 ($)"]].map(([k, label]) => (
            <label key={k}>
              <div className="overline mb-2">{label}</div>
              <input data-testid={`rail-deposit-${k}`} required type="number" step="0.01" min="0.01" placeholder="0.32"
                value={amounts[k]} onChange={(e) => setAmounts({ ...amounts, [k]: e.target.value })}
                className="w-32 px-3 py-2 border-2 border-ink font-mono text-sm" />
            </label>
          ))}
          <button data-testid="rail-verify-btn" disabled={busy} className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
            {busy ? "Verifying…" : "Confirm Deposits"}
          </button>
        </form>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rail-entry-form">
          <div className="md:col-span-2 font-mono text-[11px] text-muted-foreground flex items-start gap-2 leading-relaxed">
            <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {rail.account_masked
              ? "Entering a new account replaces the one on file, returns the rail to dormant and is written to the audit vault."
              : "You enter these numbers on the employee's behalf — the portal is read-only for them. Every edit is audited."}
          </div>
          <label className="block">
            <div className="overline mb-2">Account holder name</div>
            <input data-testid="rail-holder" required minLength={2} value={form.account_holder_name}
              onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })}
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </label>
          <label className="block">
            <div className="overline mb-2">Account type</div>
            <select data-testid="rail-type" value={form.account_type}
              onChange={(e) => setForm({ ...form, account_type: e.target.value })}
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm bg-white">
              <option value="checking">Checking</option>
              <option value="savings">Savings</option>
            </select>
          </label>
          <label className="block">
            <div className="overline mb-2">Routing number (9 digits)</div>
            <input data-testid="rail-routing" required inputMode="numeric" pattern="\d{9}" value={form.routing_number}
              onChange={(e) => setForm({ ...form, routing_number: e.target.value.replace(/\D/g, "").slice(0, 9) })}
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            <div className="mt-2 min-h-[1.25rem]">
              {lookup && (
                <div data-testid="routing-resolution" className={`font-mono text-[11px] flex items-start gap-1.5 ${
                  lookup.found ? (lookup.nexus_alert ? "text-warn" : "text-good") : "text-bad"}`}>
                  <Search className="w-3 h-3 shrink-0 mt-0.5" />
                  <span>
                    {lookup.found ? (
                      <><b data-testid="resolved-bank-name">{lookup.bank_name}</b>
                        {lookup.city ? ` · ${lookup.city}` : ""}{lookup.bank_state ? `, ${lookup.bank_state}` : ""}
                        {lookup.nexus_alert ? " · nexus mismatch" : " · verified"}</>
                    ) : lookup.message}
                  </span>
                </div>
              )}
            </div>
          </label>
          <label className="block">
            <div className="overline mb-2">Account number</div>
            <input data-testid="rail-account" required inputMode="numeric" pattern="\d{4,17}" value={form.account_number}
              onChange={(e) => setForm({ ...form, account_number: e.target.value.replace(/\D/g, "") })}
              className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </label>
          <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="font-mono text-[10px] text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Encrypted at rest · proven by Stripe micro-deposits · dormant until you authorize
            </div>
            <button data-testid="rail-submit" disabled={busy} className="hard-btn px-4 py-2 font-mono uppercase text-[10px] tracking-widest disabled:opacity-40">
              {busy ? "Saving…" : "Save & Send Micro-Deposits"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
